import { initializeApp } from 'firebase/app';
import {
  getFirestore,
  initializeFirestore,
  memoryLocalCache,
  persistentLocalCache,
  persistentMultipleTabManager,
} from 'firebase/firestore';
import {
  browserLocalPersistence,
  getAuth,
  onAuthStateChanged,
  setPersistence,
  signInAnonymously,
} from 'firebase/auth';
import { getFunctions } from 'firebase/functions';
import {
  getFirestorePersistenceState as readFirestorePersistenceState,
  initializeFirestoreWithPersistence,
} from './services/firestorePersistence';

/** Catalog Search Broker region. Must match the deployed 2nd-gen Functions. */
const FUNCTIONS_REGION = 'us-central1';

const firebaseConfig = {
  // Replace with your Firebase config
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

if (process.env.NODE_ENV === 'development') {
  console.log('Firebase config:', {
    hasApiKey: !!firebaseConfig.apiKey,
    hasAuthDomain: !!firebaseConfig.authDomain,
    hasProjectId: !!firebaseConfig.projectId,
    hasStorageBucket: !!firebaseConfig.storageBucket,
    hasMessagingSenderId: !!firebaseConfig.messagingSenderId,
    hasAppId: !!firebaseConfig.appId
  });
}

export const app = initializeApp(firebaseConfig);
const firestoreInit = initializeFirestoreWithPersistence(app, {
  initializeFirestore,
  getFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  memoryLocalCache,
});
export const db = firestoreInit.db;
export const auth = getAuth(app);
export const functions = getFunctions(app, FUNCTIONS_REGION);

export function getFirestorePersistenceState() {
  return readFirestorePersistenceState();
}

let persistenceReady = null;
let anonymousAuthReady = null;

function waitForFirstAuthState() {
  if (auth.currentUser) {
    return Promise.resolve(auth.currentUser);
  }

  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      resolve(user);
    });
  });
}

function ensureAuthPersistence() {
  if (!persistenceReady) {
    persistenceReady = setPersistence(auth, browserLocalPersistence).catch((error) => {
      persistenceReady = null;
      throw error;
    });
  }
  return persistenceReady;
}

/**
 * Drop the memoized sign-in promise.
 *
 * Required after any provider change that can replace the uid (sign-out, or
 * signing in to an existing Google account instead of linking), otherwise the
 * cached promise keeps resolving with a user object for the previous uid.
 */
export function resetAuthCache() {
  anonymousAuthReady = null;
}

/**
 * Restore any persisted anonymous user, then sign in anonymously if needed.
 * Safe to call multiple times; shares one in-flight promise.
 */
export async function ensureAnonymousAuth() {
  // A live signed-in user always wins over the memoized promise so a linked or
  // switched account is never shadowed by a stale resolution.
  if (auth.currentUser) {
    return auth.currentUser;
  }

  if (!anonymousAuthReady) {
    anonymousAuthReady = (async () => {
      await ensureAuthPersistence();
      const existing = await waitForFirstAuthState();
      if (existing) return existing;
      const { user } = await signInAnonymously(auth);
      return user;
    })().catch((error) => {
      anonymousAuthReady = null;
      throw error;
    });
  }
  return anonymousAuthReady;
}

export function getAuthUid() {
  return auth.currentUser?.uid ?? null;
}